import React, { useEffect, useState } from 'react';
import { Phone } from 'react-feather';
import { toast } from 'react-toastify';
import { currentAgentId, useTelephony } from '../../../Telephony/TelephonyProvider';
import { telephonyApi } from '../../../Telephony/telephonyApi';
import { Page, PageHeader, Card, Button, Field, TextInput, Select } from '../../../ui';
import './telephony.scss';

/**
 * Telephony → Dialer: manual dialing inside the CMS, so agents never need
 * the platform's own pages. The audio lives in the floating dock (opened on
 * arrival); this page is the keypad.
 *
 * Also the CMS-facing dial-link target: /pages/telephony-dialer?dial=%2B91…
 * lets Salesforce/Freshdesk links open the CMS — behind CMS login — instead
 * of the platform domain. Params are scrubbed from the URL immediately.
 */
/** The logged-in CMS user's own identity, as stored at login. */
function selfIdentity() {
  let email = '';
  try {
    const profile = JSON.parse(localStorage.getItem('userProfileData') || '{}');
    email = profile.emailId || profile.email || '';
  } catch {
    /* ignore */
  }
  return {
    userId: currentAgentId() || '',
    name: localStorage.getItem('loginUser') || '',
    email,
  };
}

const TelephonyDialerPage: React.FC = () => {
  const { state, placeCall, openDock, whatsappAllowed } = useTelephony();
  const [number, setNumber] = useState('');
  const [needsEnroll, setNeedsEnroll] = useState(false);
  const [teamKey, setTeamKey] = useState('sales');
  const [enrollPhone, setEnrollPhone] = useState('');
  const [enrolling, setEnrolling] = useState(false);
  const [teamKeys, setTeamKeys] = useState<string[]>(['sales', 'demo-ops', 'teacher-ops', 'cx', 'offline']);

  useEffect(() => {
    telephonyApi<{ key: string; isActive: boolean }[]>('/v1/teams')
      .then((teams) =>
        setTeamKeys(teams.filter((t) => t.isActive).map((t) => t.key)),
      )
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    const me = selfIdentity();
    if (!me.userId) return;
    telephonyApi<{ externalId: string }[]>('/v1/agents')
      .then((agents) =>
        setNeedsEnroll(!agents.some((a) => a.externalId === currentAgentId())),
      )
      .catch(() => undefined);
  }, []);

  const enroll = async () => {
    const me = selfIdentity();
    if (!me.userId || !me.name) {
      toast.error('Could not read your CMS identity — log out and in again');
      return;
    }
    if (!/^\+\d{8,15}$/.test(enrollPhone.trim())) {
      toast.warn('Enter your phone in E.164 (e.g. +919876543210) — callbacks ring there when you are offline');
      return;
    }
    setEnrolling(true);
    try {
      await telephonyApi('/v1/agents', {
        method: 'POST',
        body: JSON.stringify({
          externalId: me.userId,
          name: me.name,
          email: me.email || `${me.userId}@artiumacademy.com`,
          teamKey,
          phoneNumber: enrollPhone.trim(),
          mode: 'browser',
        }),
      });
      toast.success('Calling enabled — reloading to connect you');
      setTimeout(() => window.location.reload(), 1200);
    } catch (e) {
      toast.error((e as Error).message);
      setEnrolling(false);
    }
  };

  useEffect(() => {
    openDock();

    const params = new URLSearchParams(window.location.search);
    const dial = params.get('dial');
    if (dial) {
      let cleaned = dial.replace(/^tel:/i, '');
      if (/^\s/.test(cleaned)) cleaned = `+${cleaned.trim()}`;
      cleaned = cleaned.replace(/[\s().-]/g, '');
      // External tools (Salesforce/Freshdesk formulas) tag their links so the
      // call log keeps its origin even though the dial rides the CMS.
      const KNOWN_SOURCES = ['salesforce', 'freshchat', 'freshdesk', 'link', 'tasks'];
      const source = params.get('source') ?? '';
      const recordId = params.get('recordId') ?? undefined;
      const custUserId = params.get('userId') ?? undefined;
      params.delete('dial');
      params.delete('source');
      params.delete('recordId');
      params.delete('userId');
      const rest = params.toString();
      window.history.replaceState(
        null,
        '',
        window.location.pathname + (rest ? `?${rest}` : ''),
      );
      setNumber(cleaned);
      placeCall(cleaned, {
        source: KNOWN_SOURCES.includes(source) ? source : 'link',
        sourceRecordId: recordId,
        customerUserId: custUserId,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Meta rejects WhatsApp calls until the customer approves — this sends
  // their native Approve/Decline permission message from our WABA number.
  const requestWaPermission = async () => {
    if (!number.trim()) {
      toast.warn('Enter the customer number first');
      return;
    }
    try {
      const res = await telephonyApi<{ to: string; from: string }>(
        '/v1/calls/whatsapp/permission',
        {
          method: 'POST',
          body: JSON.stringify({ customerNumber: number.trim(), countryHint: 'IN' }),
        },
      );
      toast.success(`Permission request sent to ${res.to} on WhatsApp — once they tap Approve, WhatsApp calls connect for 7 days`);
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  const dial = (channel?: 'WHATSAPP') => {
    if (!number.trim()) {
      toast.warn('Enter a number to call');
      return;
    }
    placeCall(number.trim(), { channel });
  };

  return (
    <Page>
      <PageHeader
        eyebrow="Telephony"
        title="Dialer"
        subtitle="Manual dialing inside the CMS — no platform pages needed."
      />
      <div className="tel-dialer">
        {needsEnroll && (
          <Card
            title="Calling isn't enabled for you yet"
            subtitle={`One click sets you up using your CMS identity (${selfIdentity().name || 'unknown'}).`}
          >
            <div className="tel-form-row">
              <Field label="Your phone" required>
                {(ctl) => (
                  <TextInput
                    {...ctl}
                    value={enrollPhone}
                    placeholder="+919876543210"
                    onChange={(e) => setEnrollPhone(e.target.value)}
                  />
                )}
              </Field>
              <Field label="Your team">
                {(ctl) => (
                  <Select
                    {...ctl}
                    options={teamKeys.map((t) => ({ value: t, label: t }))}
                    value={teamKey}
                    onChange={(e) => setTeamKey(e.target.value)}
                  />
                )}
              </Field>
              <div className="tel-form-row__submit">
                <Button variant="accent" loading={enrolling} onClick={enroll}>
                  {enrolling ? 'Enabling…' : 'Enable calling for me'}
                </Button>
              </div>
            </div>
          </Card>
        )}
        <Card title="Place a call">
          <div className="ds-stack">
            <p className="tel-note">
              Calls connect in the <strong>Artium Softphone</strong> panel
              (bottom-right). It stays with you as you navigate — you can
              open a student profile mid-call.
            </p>
            <Field label="Customer number">
              {(ctl) => (
                <TextInput
                  {...ctl}
                  placeholder="+91 98765 43210"
                  value={number}
                  onChange={(e) => setNumber(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && dial()}
                />
              )}
            </Field>
            <Button variant="primary" size="lg" block icon={<Phone size={18} aria-hidden />} onClick={() => dial()}>
              Call
            </Button>
            {whatsappAllowed && (
              <Button
                variant="secondary"
                size="lg"
                block
                icon={<i className="fa fa-whatsapp tel-whatsapp-icon" aria-hidden />}
                onClick={() => dial('WHATSAPP')}
              >
                WhatsApp call
              </Button>
            )}
            {whatsappAllowed && (
              <Button variant="ghost" size="sm" block onClick={requestWaPermission}>
                Request WhatsApp call permission
              </Button>
            )}
            <p className="tel-note">
              Status: <strong>{state}</strong> — Indian customers see an
              Indian number, international customers a local one, chosen
              automatically.
            </p>
          </div>
        </Card>
      </div>
    </Page>
  );
};

export default TelephonyDialerPage;
